/**
 * Action Modal Block Converter
 *
 * Converts production DB blocks (song_modal_blocks, song_pathway_cards)
 * to frontend ActionModalBlock types for rendering.
 */

import type { ActionModalBlock } from './actionModalBlockTypes';
import type { ModalBlock, PathwayCard } from './actionModalProductionService';

/**
 * Convert a production modal block to a frontend ActionModalBlock
 */
export function convertModalBlockToFrontend(dbBlock: ModalBlock): ActionModalBlock | null {
  const { block_type, block_title, block_body, block_prompt, items, content } = dbBlock;

  switch (block_type) {
    case 'richText':
      return {
        type: 'richText',
        title: block_title || undefined,
        body: block_body || '',
      };

    case 'prompt':
      return {
        type: 'reflectionPrompt',
        title: block_title || 'Reflect',
        prompt: block_prompt || block_body || '',
        placeholder: 'Your thoughts...',
      };

    case 'callout':
      // Callouts are rich text with a highlighted style
      return {
        type: 'insightCard',
        title: block_title || undefined,
        statement: block_body || '',
        supportingPoints: items || undefined,
      };

    case 'checklist':
      return {
        type: 'checklist',
        title: block_title || undefined,
        items: (items || []).map((text) => ({ text })),
      };

    case 'insight_chips':
      // Explorer-specific insight chips
      if (content && typeof content === 'object' && 'chips' in content) {
        return {
          type: 'insightChips',
          chips: (content as any).chips || [],
        };
      }
      return null;

    default:
      console.warn(`[convertModalBlockToFrontend] Unknown block type: ${block_type}`);
      return null;
  }
}

/**
 * Convert pathway cards to Take It Further action cards
 */
export function convertPathwayCardsToBlocks(cards: PathwayCard[]): ActionModalBlock[] {
  if (cards.length === 0) {
    return [{
      type: 'placeholder',
      title: 'No Actions Yet',
      body: 'Action pathways for this level are being developed.',
    }];
  }

  // Group cards into a pathway selector block
  return [{
    type: 'pathwaySelector',
    title: 'Choose Your Path',
    description: 'Pick one pathway that interests you.',
    pathways: cards.map(card => ({
      id: card.id,
      label: card.title,
      description: card.description,
      icon: undefined,
    })),
  }];
}
